import { SelectionProviderChildProps } from '@tableau/data-catalog-shared';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import { Messages } from '../../../gen-src/Localize';
import { TableQuery_tables } from '../../../gen-src/queries/TableQuery';
import { LineageItemType } from '../lineage/LineageItemType';
import { LineageItemConfig, LineageSection, LineageSectionType } from '../placePage/LineageConfiguration';
import { PaneWithTitle } from '../placePage/PaneWithTitle';
import { DashboardsGridSortProvider } from '../sorting/sortProviders/DashboardsGridSortProvider';
import { DatabasesGridSortProvider } from '../sorting/sortProviders/DatabasesGridSortProvider';
import { DatasourcesGridSortProvider } from '../sorting/sortProviders/DatasourcesGridSortProvider';
import { FlowsGridSortProvider } from '../sorting/sortProviders/FlowsGridSortProvider';
import { MetricsGridSortProvider } from '../sorting/sortProviders/MetricsGridSortProvider';
import { SheetsGridSortProvider } from '../sorting/sortProviders/SheetsGridSortProvider';
import { TablesGridSortProvider } from '../sorting/sortProviders/TablesGridSortProvider';
import { VirtualConnectionTablesGridSortProvider } from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import { VirtualConnectionsGridSortProvider } from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';
import { WorkbooksGridSortProvider } from '../sorting/sortProviders/WorkbooksGridSortProvider';
import { TableColumnsPane } from './TableColumnsPane';
import { TableDownstreamDashboardsQueryGrid } from './TableDownstreamDashboardsQueryGrid';
import { TableDownstreamDatabasesQueryGrid } from './TableDownstreamDatabasesQueryGrid';
import { TableDownstreamDatasourcesQueryGrid } from './TableDownstreamDatasourcesQueryGrid';
import { TableDownstreamFlowsQueryGrid } from './TableDownstreamFlowsQueryGrid';
import { TableDownstreamMetricsQueryGrid } from './TableDownstreamMetricsQueryGrid';
import { TableDownstreamOwnersPane } from './TableDownstreamOwnersPane';
import { TableDownstreamSheetsQueryGrid } from './TableDownstreamSheetsQueryGrid';
import { TableDownstreamTablesQueryGrid } from './TableDownstreamTablesQueryGrid';
import { TableDownstreamVirtualConnectionTablesQueryGrid } from './TableDownstreamVirtualConnectionTablesQueryGrid';
import { TableDownstreamVirtualConnectionsQueryGrid } from './TableDownstreamVirtualConnectionsQueryGrid';
import { TableDownstreamWorkbooksQueryGrid } from './TableDownstreamWorkbooksQueryGrid';
import { TableLineageCounts } from './TableLineageCountsQueryWrapper';
import { TableUpstreamDatabasesQueryGrid } from './TableUpstreamDatabasesQueryGrid';
import { TableUpstreamDatasourcesQueryGrid } from './TableUpstreamDatasourcesQueryGrid';
import { TableUpstreamFlowsQueryGrid } from './TableUpstreamFlowsQueryGrid';
import { TableUpstreamTablesQueryGrid } from './TableUpstreamTablesQueryGrid';

export type TableLineageItemConfig = LineageItemConfig<TableLineageCounts, TableLineagePaneParams>;
export type TableLineageSection = LineageSection<TableLineageCounts, TableLineagePaneParams>;

export type TableLineagePaneParams = SelectionProviderChildProps & {
  readonly title: string;
  readonly table: TableQuery_tables;
  readonly tableId: string;
  readonly filteredColumnId: string | null;
  readonly count: number | null;
  readonly hackyUpdateNumber: number;
};

const TableUpstreamItems: ReadonlyArray<TableLineageItemConfig> = [
  {
    lineageItemType: LineageItemType.Database,
    messagesForLineage: { withCount: Messages.DatabasesWithCount, withoutCount: Messages.Databases },
    messagesForPane: { withCount: Messages.UpstreamDatabasesWithCount, withoutCount: Messages.UpstreamDatabases },
    count: counts => counts.upstreamDatabases,
    path: (urls, id) => urls.catalogTableUpstreamDatabasesPath(id),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <DatabasesGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <TableUpstreamDatabasesQueryGrid
              tableId={paneParams.tableId}
              filteredColumnId={paneParams.filteredColumnId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />
          }
        </DatabasesGridSortProvider>
      </PaneWithTitle>
    ),
  },
  {
    lineageItemType: LineageItemType.Table,
    messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
    messagesForPane: { withCount: Messages.UpstreamTablesWithCount, withoutCount: Messages.UpstreamTables },
    count: counts => counts.upstreamTables,
    path: (urls, id) => urls.catalogTableUpstreamTablesPath(id),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <TablesGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <TableUpstreamTablesQueryGrid
              tableId={paneParams.tableId}
              filteredColumnId={paneParams.filteredColumnId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />
          }
        </TablesGridSortProvider>
      </PaneWithTitle>
    ),
  },
  {
    lineageItemType: LineageItemType.Flow,
    messagesForLineage: { withCount: Messages.FlowsWithCount, withoutCount: Messages.Flows },
    messagesForPane: { withCount: Messages.UpstreamFlowsWithCount, withoutCount: Messages.UpstreamFlows },
    count: counts => counts.upstreamFlows,
    path: (urls, id) => urls.catalogTableUpstreamFlowsPath(id),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <FlowsGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <TableUpstreamFlowsQueryGrid
              tableId={paneParams.tableId}
              filteredColumnId={paneParams.filteredColumnId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />
          }
        </FlowsGridSortProvider>
      </PaneWithTitle>
    ),
  },
  {
    lineageItemType: LineageItemType.Datasource,
    messagesForLineage: { withCount: Messages.DatasourcesWithCount, withoutCount: Messages.Datasources },
    messagesForPane: { withCount: Messages.UpstreamDatasourcesWithCount, withoutCount: Messages.UpstreamDatasources },
    count: counts => counts.upstreamDatasources,
    path: (urls, id) => urls.catalogTableUpstreamDatasourcesPath(id),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <DatasourcesGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <TableUpstreamDatasourcesQueryGrid
              tableId={paneParams.tableId}
              filteredColumnId={paneParams.filteredColumnId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />}
        </DatasourcesGridSortProvider>
      </PaneWithTitle>
    ),
  },
];

const TableAnchor: TableLineageItemConfig = {
  lineageItemType: LineageItemType.Table,
  messagesForLineage: { withCount: Messages.ColumnsWithCount, withoutCount: Messages.Columns },
  messagesForPane: { withCount: Messages.ColumnsWithCount, withoutCount: Messages.Columns },
  count: counts => counts.columns,
  path: (urls, tableId) => urls.catalogTableColumnsPath(tableId),
  paneFactory: (paneParams) => (
    <TableColumnsPane
      title={paneParams.title}
      table={paneParams.table}
      tableId={paneParams.tableId}
      selectedKeys={paneParams.selectedKeys}
      updateSelectionForKey={paneParams.updateSelectionForKey}
      rowCount={paneParams.count}
      hackyUpdateNumber={paneParams.hackyUpdateNumber}
    />
  )
};

const tableDownstreamMetrics: TableLineageItemConfig = {
  lineageItemType: LineageItemType.Metric,
  messagesForLineage: { withCount: Messages.MetricsWithCount, withoutCount: Messages.Metrics },
  messagesForPane: { withCount: Messages.DownstreamMetricsWithCount, withoutCount: Messages.DownstreamMetrics },
  count: counts => counts.downstreamMetrics,
  path: (urls, tableId) => urls.catalogTableDownstreamMetricsPath(tableId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <MetricsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <TableDownstreamMetricsQueryGrid
            tableId={paneParams.tableId}
            filteredColumnId={paneParams.filteredColumnId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />}
      </MetricsGridSortProvider>
    </PaneWithTitle>
  )
};
const tableDownstreamVirtualConnection: TableLineageItemConfig = {
  lineageItemType: LineageItemType.VirtualConnection,
  messagesForLineage: { withCount: Messages.VirtualConnectionsWithCount, withoutCount: Messages.VirtualConnections },
  messagesForPane: { withCount: Messages.UpstreamVirtualConnectionsWithCount, withoutCount: Messages.UpstreamVirtualConnections },
  count: counts => counts.downstreamVirtualConnections,
  path: (urls, vizportalUrlId) => urls.catalogTableDownstreamVirtualConnectionsPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <VirtualConnectionsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <TableDownstreamVirtualConnectionsQueryGrid
            tableId={paneParams.tableId}
            filteredColumnId={paneParams.filteredColumnId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />
        }
      </VirtualConnectionsGridSortProvider>
    </PaneWithTitle>
  ),
};

const tableDownstreamVirtualConnectionTable: TableLineageItemConfig = {
  lineageItemType: LineageItemType.VirtualConnectionTable,
  messagesForLineage: { withCount: Messages.VirtualConnectionTablesWithCount, withoutCount: Messages.VirtualConnectionTables },
  messagesForPane: { withCount: Messages.UpstreamVirtualConnectionTablesWithCount, withoutCount: Messages.UpstreamVirtualConnectionTables },
  count: counts => counts.downstreamVirtualConnectionTables,
  path: (urls, vizportalUrlId) => urls.catalogTableDownstreamVirtualConnectionTablesPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <VirtualConnectionTablesGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <TableDownstreamVirtualConnectionTablesQueryGrid
            tableId={paneParams.tableId}
            filteredColumnId={paneParams.filteredColumnId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />
        }
      </VirtualConnectionTablesGridSortProvider>
    </PaneWithTitle>
  ),
};

function getTableDownstreamItems(metricsDataCatalogEnabled: boolean, publishedConnectionsLineageEnabled: boolean): ReadonlyArray<TableLineageItemConfig> {
  return [
    {
      lineageItemType: LineageItemType.Database,
      messagesForLineage: { withCount: Messages.DatabasesWithCount, withoutCount: Messages.Databases },
      messagesForPane: { withCount: Messages.DownstreamDatabasesWithCount, withoutCount: Messages.DownstreamDatabases },
      count: counts => counts.downstreamDatabases,
      path: (urls, databaseId) => urls.catalogTableDownstreamDatabasesPath(databaseId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatabasesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <TableDownstreamDatabasesQueryGrid
                tableId={paneParams.tableId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </DatabasesGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Table,
      messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
      messagesForPane: { withCount: Messages.DownstreamTablesWithCount, withoutCount: Messages.DownstreamTables },
      count: counts => counts.downstreamTables,
      path: (urls, databaseId) => urls.catalogTableDownstreamTablesPath(databaseId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <TablesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <TableDownstreamTablesQueryGrid
                tableId={paneParams.tableId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </TablesGridSortProvider>
        </PaneWithTitle>
      )
    },
    ...(publishedConnectionsLineageEnabled ? [tableDownstreamVirtualConnection, tableDownstreamVirtualConnectionTable] : []),
    {
      lineageItemType: LineageItemType.Flow,
      messagesForLineage: { withCount: Messages.FlowsWithCount, withoutCount: Messages.Flows },
      messagesForPane: { withCount: Messages.DownstreamFlowsWithCount, withoutCount: Messages.DownstreamFlows },
      count: counts => counts.downstreamFlows,
      path: (urls, databaseId) => urls.catalogTableDownstreamFlowsPath(databaseId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <FlowsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <TableDownstreamFlowsQueryGrid
                tableId={paneParams.tableId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </FlowsGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Datasource,
      messagesForLineage: { withCount: Messages.DatasourcesWithCount, withoutCount: Messages.Datasources },
      messagesForPane: { withCount: Messages.DownstreamDatasourcesWithCount, withoutCount: Messages.DownstreamDatasources },
      count: counts => counts.downstreamDatasources,
      path: (urls, tableId) => urls.catalogTableDownstreamDatasourcesPath(tableId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatasourcesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <TableDownstreamDatasourcesQueryGrid
                tableId={paneParams.tableId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DatasourcesGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Workbook,
      messagesForLineage: { withCount: Messages.WorkbooksWithCount, withoutCount: Messages.Workbooks },
      messagesForPane: { withCount: Messages.DownstreamWorkbooksWithCount, withoutCount: Messages.DownstreamWorkbooks },
      count: counts => counts.downstreamWorkbooks,
      path: (urls, tableId) => urls.catalogTableDownstreamWorkbooksPath(tableId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <WorkbooksGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <TableDownstreamWorkbooksQueryGrid
                tableId={paneParams.tableId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </WorkbooksGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Sheet,
      messagesForLineage: { withCount: Messages.SheetsWithCount, withoutCount: Messages.Sheets },
      messagesForPane: { withCount: Messages.DownstreamSheetsWithCount, withoutCount: Messages.DownstreamSheets },
      count: counts => counts.downstreamSheets,
      path: (urls, tableId) => urls.catalogTableDownstreamSheetsPath(tableId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <SheetsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <TableDownstreamSheetsQueryGrid
                tableId={paneParams.tableId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </SheetsGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Dashboard,
      messagesForLineage: { withCount: Messages.DashboardsWithCount, withoutCount: Messages.Dashboards },
      messagesForPane: { withCount: Messages.DownstreamDashboardsWithCount, withoutCount: Messages.DownstreamDashboards },
      count: counts => counts.downstreamDashboards,
      path: (urls, tableId) => urls.catalogTableDownstreamDashboardsPath(tableId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DashboardsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <TableDownstreamDashboardsQueryGrid
                tableId={paneParams.tableId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DashboardsGridSortProvider>
        </PaneWithTitle>
      )
    },
    ...(metricsDataCatalogEnabled ? [tableDownstreamMetrics] : []),
  ];
}
const memoizedGetTableDownstreamItems = defaultMemoize(getTableDownstreamItems);

const TableOwners: TableLineageItemConfig = {
  lineageItemType: LineageItemType.User,
  messagesForLineage: { withCount: Messages.OwnersWithCount, withoutCount: Messages.Owners },
  messagesForPane: { withCount: Messages.DownstreamOwnersWithCount, withoutCount: Messages.DownstreamOwners },
  count: counts => counts.downstreamOwners,
  path: (urls, tableId) => urls.catalogTableDownstreamOwnersPath(tableId),
  paneFactory: (paneParams) => (
    <TableDownstreamOwnersPane
      title={paneParams.title}
      table={paneParams.table}
      tableId={paneParams.tableId}
      filteredColumnId={paneParams.filteredColumnId}
      rowCount={paneParams.count}
    />
  )
};

function getTableLineageConfiguration(metricsDataCatalogEnabled: boolean, publishedConnectionsLineageEnabled: boolean): ReadonlyArray<TableLineageSection> {
  return [
    {
      sectionType: LineageSectionType.Group,
      key: 'upstream',
      items: TableUpstreamItems,
    },
    {
      sectionType: LineageSectionType.Anchor,
      key: 'anchor',
      items: [TableAnchor],
    },
    {
      sectionType: LineageSectionType.Group,
      key: 'downstream',
      items: memoizedGetTableDownstreamItems(metricsDataCatalogEnabled, publishedConnectionsLineageEnabled),
    },
    {
      sectionType: LineageSectionType.Owners,
      key: 'owners',
      items: [TableOwners],
    },
  ];
}
export const memoizedGetTableLineageConfiguration = defaultMemoize(getTableLineageConfiguration);
